/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Box, Button, Checkbox, Divider, Link, Select, Spinner, TextArea, Typography } from '@telus-uds/components-web';

import Download from '@telus-uds/palette-allium/build/web/icons/Download';

import EN from '../../../EN.json';
import FR from '../../../FR.json';
import {
  SummaryWrapper,
  DefectAttachmentRow,
  DefectSummaryFirstCol,
  DefectSummaryRow,
  DefectSummarySecondCol,
  OrderDetailsBox
} from '../css';
import DefectSummaryHeader from './DefectSummaryHeader';
import { useLocaleContext } from '../../context/LocaleContext';
import { GET_DEFECT_LOCALE, GET_WORKING_DAYS } from '../constants';

import request, { isAdmin } from '../../utils/request';
import DefectAssignee from './DefectAssignee';
import DefectComments from './DefectComments';

const DefectSummary = ({
  setFetchData,
  openModal,
  defect,
  userProfile,
  defectId
}) => {
  const [count, setCount] = useState(0);
  const locale = useLocaleContext();
  const [defectData, setDefectData] = useState(defect);
  const [hasError, setHasError] = useState(false);
  const [errorTitle, setErrorTitle] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const {
    DEFECT_MANAGEMENT: {
      DEFECT_SUMMARY = '',
      ATTACHMENTS = '',
      SUMMARY_TABLE = {},
      COMMENTS = '',
      ADD_COMMENT = '',
      ADD_BUTTON = '',
      FILTER_BY_STATUS = {},
      STATUS_OPTIONS,
      DEFAULT_STATUS_OPTIONS,
      NO_ATTACHMENTS = '',
      CHANGE_STATUS = '',
      FOR_ADMINS_ONLY = '',
      MAX_1000_CHARS
    } = {}
  } = locale === 'en-US' ? EN : FR;
  const {
    defectWebform = '',
    createdAt = '',
    priority = '',
    id = '',
    reportIssueAttachments = '',
    status: defectStatus,
    comments = '',
    new_update_for_admin = '',
    new_update_for_reporter = ''
  } = defectData || {};

  const comment = React.createRef();

  const [assignee, setAssignee] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState([]);
  const [isSubmitting, setSubmitting] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [isAdminOnly, setIsAdminOnly] = useState(false);
  const [assigneeModal, setAssigneeModal] = useState(false);

  const updatedDefects = comments
    ? { ...defectData }
    : { ...defectData, comments: 'No comments' };

  // states
  const [updatedComment, setUpdatedComment] = useState('');
  const [status, setStatus] = useState('');
  const [commentsList, setCommentsList] = useState(
    defectData.defectComments || []
  );

  let moved_to_in_progress_at = '';
  let resolved_at = '';

  useEffect(() => {
    const assigneeObject = defectData.defectAssignedTo
      ? {
          id: defectData.assigned_user_id,
          label:
            defectData.defectAssignedTo &&
            (defectData.defectAssignedTo.firstName ||
              defectData.defectAssignedTo.lastName) &&
            `${defectData.defectAssignedTo.firstName} ${defectData.defectAssignedTo.lastName}`
        }
      : { id: 0, label: '' };
    setAssignee([assigneeObject] || []);
    setSelectedAssignee([assigneeObject]);
    if (defectData.moved_to_in_progress_at) {
      setCount(GET_WORKING_DAYS(defectData.moved_to_in_progress_at));
    }
  }, [defectData]);

  const updatedDefectComment = () => {
    if (updatedComment && updatedComment.length > 0) {
      // Commenter identity (email, name, role) is set by the server from the session.
      const body = {
        text: updatedComment,
        is_admin_only: isAdminOnly
      };
      setSubmitting(true);
      request
        .post(`/defects/${id}/comments`, body)
        .then(res => {
          const { data = {} } = res;
          if (data.success === 1) {
            openModal(false);
            setFetchData(true);
          }
        })
        .catch(err => new Error(err))
        .finally(() => {
          setSubmitting(false);
        });
    }
  };

  const updateDefectAdminNotification = () => {
    if (!id) return;
    let notificationPath = '';
    if (isAdmin && new_update_for_admin === true) {
      notificationPath = 'admin-notification';
    } else if (!isAdmin && new_update_for_reporter === true) {
      notificationPath = 'reporter-notification';
    }
    if (notificationPath) {
      request
        .put(`/defects/${id}/${notificationPath}`, {})
        .then(() => {})
        .catch(err => new Error(err));
    }
  };

  const getCurrentDefect = () => {
    request
      .get(`/defects/${id || defectId}`)
      .then(response => {
      if (response && response.status === 200) {
        if (response.data.innerData) {
          setHasError(false);
          setDefectData(response.data.innerData);
          setCommentsList(response.data.innerData.defectComments || []);
          updateDefectAdminNotification();
        } else {
          setHasError(true);
          setErrorTitle('');
          setErrorMessage('No Record Found');
        }
      } else {
        setHasError(true);
        setErrorTitle('Access Denied!');
        setErrorMessage(`You don't have permission to view this Defect`);
      }
      })
      .catch(() => {
        // The server answers 404 for defects the user cannot see.
        setHasError(true);
        setErrorTitle('');
        setErrorMessage('No Record Found');
      });
  };

  const getDefectAdmins = () => {
    request.get('/defects/admins').then(response => {
      if (response && response.data) {
        const adminUsersData =
          response.data.adminList &&
          response.data.adminList.length > 0 &&
          response.data.adminList.reduce(
            (acc, { id: adminId, firstName, lastName, email }) => {
              return [
                ...acc,
                { id: adminId, label: `${firstName} ${lastName}`, email }
              ];
            },
            []
          );
        setAdminUsers(adminUsersData);
      }
    });
  };

  useEffect(() => {
    getCurrentDefect();
    // The admin list only feeds the assignee picker, which only admins can open.
    if (isAdmin) getDefectAdmins();
  }, [defectId]);

  useEffect(() => {
    if (defectData.moved_to_in_progress_at) {
      setCount(GET_WORKING_DAYS(defectData.moved_to_in_progress_at));
    }
  }, []);

  const updatedDefectStatus = () => {
    if (isAdmin && updatedComment) {
      updatedDefectComment();
    }
    if (assignee && assignee.length > 0 && assignee[0].label) {
      if (status) {
        if (status === STATUS_OPTIONS[1]) {
          // In Progress
          moved_to_in_progress_at = moment.utc().format();
        }
        if (status === STATUS_OPTIONS[2] || status === STATUS_OPTIONS[3]) {
          // Resolved || Closed - No Response
          resolved_at = moment.utc().format();
        }
        const body = {
          status,
          moved_to_in_progress_at,
          resolved_at,
          // new_update_for_admin: true,
          new_update_for_reporter: true
        };
        setSubmitting(true);
        request
          .post(`/defects/${id}/status`, body)
          .then(res => {
            const { data = {} } = res;
            if (data.success === 1) {
              openModal(false);
              setFetchData(true);
            }
          })
          .catch(err => new Error(err))
          .finally(() => {
            setSubmitting(false);
          });
      }
    }
  };

  const onAssigneeChange = selectedValue => {
    setSelectedAssignee(selectedValue || []);
  };

  const onClickSave = () => {
    if (selectedAssignee[0]) {
      const body = {
        assigned_user_id: selectedAssignee[0] && selectedAssignee[0].email,
        new_update_for_admin: true
      };
      request
        .put(`/defects/${defectData.id}/assignee`, body)
        .then(() => {
          setAssignee(selectedAssignee);
        })
        .catch(err => new Error(err));
    }
  };

  const getUpdatedList = () => {
    if (count >= 5) return STATUS_OPTIONS;
    return DEFAULT_STATUS_OPTIONS;
  };

  return (
    <>
      {!hasError ? (
        <SummaryWrapper>
          <Box bottom={4}>
            <OrderDetailsBox>
              <DefectSummaryHeader
                priority={GET_DEFECT_LOCALE(priority)}
                status={GET_DEFECT_LOCALE(defectStatus)}
                reportedOn={`${moment
                  .utc(createdAt)
                  .format('dddd, MMMM Do YYYY, h:mm:ss a')} UTC`}
                webForm={GET_DEFECT_LOCALE(defectWebform)}
                assignee={assignee}
                isAdmin={isAdmin}
                setAssigneeModal={setAssigneeModal}
              />
            </OrderDetailsBox>
              <Divider space={4} variant={{ inverse: true }} />
            <Box bottom={3}>
               <Typography heading="h2" variant={{ size: 'h2' }} block>
                 {DEFECT_SUMMARY}
               </Typography>
              {updatedDefects &&
                SUMMARY_TABLE &&
                Object.keys(SUMMARY_TABLE).map(key => {
                  const selectedLabel = SUMMARY_TABLE[key];
                  return (
                    <DefectSummaryRow>
                      <DefectSummaryFirstCol>
                        {selectedLabel}
                      </DefectSummaryFirstCol>
                      <DefectSummarySecondCol>
                        {key === 'createdAt'
                          ? `${moment
                              .utc(createdAt)
                              .format('dddd, MMMM Do YYYY, h:mm:ss a')} UTC`
                          : GET_DEFECT_LOCALE(
                              updatedDefects[key]
                                ? updatedDefects[key]
                                : '-'
                            )}
                      </DefectSummarySecondCol>
                    </DefectSummaryRow>
                  );
                })}
            </Box>
              <Divider space={3} variant={{ inverse: true }} />
            <Box bottom={4}>
               <Typography heading="h2" variant={{ size: 'h2' }} block>
                 {ATTACHMENTS}
               </Typography>
                <Divider space={1} variant={{ inverse: true }} />
              <DefectAttachmentRow>
                <div className="row">
                  <div className="col-8">
                    {reportIssueAttachments || NO_ATTACHMENTS}
                  </div>
                  {reportIssueAttachments && (
                    <div className="col-4" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <Link
                        href={reportIssueAttachments}
                        icon={Download}
                        iconPosition="left"
                        hrefAttrs={{ download: true }}
                      />
                    </div>
                  )}
                </div>
              </DefectAttachmentRow>
                <Divider space={3} variant={{ inverse: true }} />
            </Box>
            <Box bottom={4}>
               <Typography heading="h2" variant={{ size: 'h2' }} block>
                 {COMMENTS}
               </Typography>
              {commentsList && commentsList.length > 0 && (
                <DefectComments data={commentsList} isAdmin={isAdmin} />
              )}
                <Divider space={2} variant={{ inverse: true }} />
              <div
                style={{
                  textAlign: 'center',
                  marginTop: '10px',
                  marginBottom: '10px'
                }}
              >
                <div>
                  <TextArea
                    label={ADD_COMMENT}
                    ref={comment}
                    value={updatedComment}
                    maxLength={1000}
                    onChange={(value) => {
                      setUpdatedComment(value);
                    }}
                  />
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '900px',
                      color: '#ABABAB',
                      display: 'flex',
                      flexDirection: 'row'
                    }}
                  >
                    {MAX_1000_CHARS}
                  </span>
                </div>
                {isAdmin && (
                  <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                    <Checkbox
                      checked={isAdminOnly}
                      name="AdminOnly"
                      value="adminOnly"
                      onChange={() => {
                        setIsAdminOnly(!isAdminOnly);
                      }}
                      label={FOR_ADMINS_ONLY}
                    />
                  </div>
                )}
                {!isAdmin && (
                  <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                    <Spinner
                      show={isSubmitting}
                      variant={{ size: 'small' }}
                      label="Request is processing."
                      inline
                    >
                      <Button
                        key={1}
                        variant={{ priority: 'low' }}
                        onPress={updatedDefectComment}
                      >
                        {ADD_BUTTON}
                      </Button>
                    </Spinner>
                  </div>
                )}
              </div>
                <Divider space={4} variant={{ inverse: true }} />
            </Box>
            {assigneeModal && adminUsers.length > 0 && (
              <DefectAssignee
                assignee={assignee}
                selectedAssignee={selectedAssignee}
                adminUsers={adminUsers}
                onAssigneeChange={onAssigneeChange}
                onClickSave={onClickSave}
                setAssigneeModal={setAssigneeModal}
              />
            )}
            {userProfile.role.name === 'ADMIN' ? (
              <Box bottom={4}>
                  <Divider space={2} variant={{ inverse: true }} />
                <Select
                  label={CHANGE_STATUS}
                  placeholder={FILTER_BY_STATUS.placeholder}
                  disabled={
                    !(assignee && assignee.length > 0 && assignee[0].label)
                  }
                  value={status}
                  onChange={(value) => {
                    setStatus(value);
                  }}
                >
                  {getUpdatedList() && getUpdatedList().map(o => (
                    <Select.Item key={o} value={o}>
                      {o}
                    </Select.Item>
                  ))}
                </Select>
                <div
                  style={{
                    textAlign: 'center',
                    marginTop: '10px',
                    marginBottom: '10px'
                  }}
                >
                  <Spinner
                    show={isSubmitting}
                    variant={{ size: 'small' }}
                    label="Request is processing."
                    inline
                  >
                    <Button
                      key={2}
                      variant={{ priority: 'low' }}
                      onPress={updatedDefectStatus}
                    >
                      {ADD_BUTTON}
                    </Button>
                  </Spinner>
                </div>
                  <Divider space={4} variant={{ inverse: true }} />
              </Box>
            ) : (
              ''
            )}
          </Box>
        </SummaryWrapper>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            height: '500px'
          }}
        >
          <span>{errorTitle}</span>
          <span>{errorMessage}</span>
        </div>
      )}
    </>
  );
};

DefectSummary.propTypes = {
  userProfile: PropTypes.shape({
    role: PropTypes.shape({
      name: PropTypes.string
    })
  }).isRequired,
  defectId: PropTypes.string.isRequired,
  defect: PropTypes.objectOf(PropTypes.any).isRequired,
  openModal: PropTypes.func.isRequired,
  setFetchData: PropTypes.func.isRequired
};

export default DefectSummary;
